import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useConfigValue } from '~/shared/utils/config';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { LAYOUT_TYPE } from '../../../constants';
import {
  generateFilterImageUrl,
  getFacetOptionRBKey,
} from '../../../utils/filters';

import DisabledOptionWrapper from './DisabledOptionWrapper';

const DynamicFormattedMessage = FormattedMessage;

const FilterOptionsList = styled.ul<{ $useGridLayout: boolean }>`
  display: ${({ $useGridLayout }) => ($useGridLayout ? 'grid' : 'flex')};
  grid-template-columns: repeat(2, minmax(120px, 1fr));
  gap: 8px;
  flex-wrap: wrap;
  list-style: none;
`;

const FilterOption = styled.button<{ $useGridLayout: boolean }>`
  min-width: 64px;
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    border-color 0.2s,
    background-color 0.2s;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: ${({ $useGridLayout }) =>
    $useGridLayout ? 'normal' : 'flex-start'};
  gap: 12px;
  text-align: left;
  font-family: inherit;
  font-size: 14px;
  width: 100%;

  /* Gate hover and active states on hover-capable devices — on touch,
     :active can linger after tap when the DOM updates mid-tap, leaving the
     option stuck in its press style. */
  @media (hover: hover) {
    &:hover {
      border-color: ${({ theme }) =>
        theme.colors.gridV2UnselectedFilterBtnBorderHover};
    }

    &:active {
      border-color: ${({ theme }) =>
        theme.colors.gridV2UnselectedFilterBtnBorderActive};
    }
  }

  &[aria-selected='true'] {
    border-color: ${({ theme }) => theme.colors.gridV2SelectedFilterBtnBorder};
    font-weight: 700;

    @media (hover: hover) {
      &:hover {
        background-color: ${({ theme }) =>
          theme.colors.gridV2SelectedFilterBtnBgHover};
      }

      &:active {
        background-color: ${({ theme }) =>
          theme.colors.gridV2SelectedFilterBtnBgActive};
      }
    }
  }
`;

const ImageSwatch = styled.span<{ $imageUrl: string }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid black;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-image: ${({ $imageUrl }) =>
    $imageUrl ? `url('${$imageUrl}')` : 'none'};
`;

const FilterOptionsWithImage = ({
  facetName,
  options,
  onClick,
  isSelected,
  isDisabled,
  layoutType = LAYOUT_TYPE.FILTER_PANEL,
}) => {
  const filterImageCdnUrl = useConfigValue('public.filterImageCdnUrl');

  return (
    <FilterOptionsList $useGridLayout={layoutType === LAYOUT_TYPE.FILTER_PANEL}>
      {options.map((option) => {
        const facetValue = option.value;
        const selected = isSelected(facetValue);
        const imageUrl = generateFilterImageUrl(
          facetName,
          facetValue,
          filterImageCdnUrl
        );

        const disabled = isDisabled?.(facetValue);

        return (
          <DisabledOptionWrapper
            key={facetValue}
            $disabled={disabled}
            aria-disabled={disabled || undefined}
          >
            <FilterOption
              $useGridLayout={layoutType === LAYOUT_TYPE.FILTER_PANEL}
              onClick={() => onClick(facetValue)}
              aria-selected={selected}
              data-autotag={`filter-${facetName}-option`}
            >
              <ImageSwatch $imageUrl={imageUrl} aria-hidden="true" />
              <DynamicFormattedMessage
                id={`grid_dynamic.${getFacetOptionRBKey(
                  facetName,
                  facetValue
                )}`}
                defaultMessage={option.display_name}
              />
            </FilterOption>
          </DisabledOptionWrapper>
        );
      })}
    </FilterOptionsList>
  );
};

FilterOptionsWithImage.propTypes = {
  facetName: PropTypes.string.isRequired,
  isDisabled: PropTypes.func,
  isSelected: PropTypes.func.isRequired,
  layoutType: PropTypes.oneOf(Object.values(LAYOUT_TYPE)),
  onClick: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string,
      value: PropTypes.string,
    })
  ).isRequired,
};

export default FilterOptionsWithImage;
